import {
  Component,
  OnInit,
  LOCALE_ID,
  EventEmitter,
  Output,
  Input,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { ToastService } from '@app/core/services/toast/toast.service';
import {
  CartonGroupingSearchAttributes,
  CartonGroupingsGQL,
  CartonGroupingsQueryVariables,
} from '@app/generated/graphql';
import { DecimalPipe, TitleCasePipe, NgClass } from '@angular/common';
import { Router } from '@angular/router';
import { CartonGroupingViewModel } from '@app/core/view-models/carton-grouping.viewmodel';
import { ExcelHelper } from '@app/core/helpers/excel.helper';
import { Angulartics2 } from 'angulartics2';
import {
  AggregatedColumn,
  ColumnMapping,
} from '@app/core/components-openagri/agx-pivot-table/agx-pivot-table.component';
import { Aggregators } from '@app/core/components-openagri/agx-pivot-table/aggregators';
import { AgxPivotTableComponent } from '../../components-openagri/agx-pivot-table/agx-pivot-table.component';
import { ShimmerLoaderComponent } from '../../../shared/shimmer-loader/shimmer-loader.component';

@Component({
  selector: 'ag-carton-grouping-pivot-table',
  templateUrl: './carton-grouping-pivot-table.component.html',
  styleUrls: ['./carton-grouping-pivot-table.component.css'],
  providers: [TitleCasePipe],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [AgxPivotTableComponent, NgClass, ShimmerLoaderComponent],
})
export class CartonGroupingPivotTableComponent implements OnInit {
  locale = inject(LOCALE_ID);
  private cartonGroupingsGql = inject(CartonGroupingsGQL);
  private toastService = inject(ToastService);
  private router = inject(Router);
  private angulartics2 = inject(Angulartics2);
  private titleCase = inject(TitleCasePipe);

  @Output()
  loading: EventEmitter<boolean> = new EventEmitter();

  @Output()
  canDownload: EventEmitter<boolean> = new EventEmitter();

  @Input()
  companyId: number;

  @Input()
  companyIds: number[];

  @Input()
  orderId: number;

  @Input()
  packingWeekFrom: string;

  @Input()
  packingWeekTo: string;

  @Input()
  limit: number;

  @Input()
  loadAtStart = false;

  _selectedColumns: ColumnMapping[];

  @Output()
  selectedColumnsChange = new EventEmitter<ColumnMapping[]>();

  @Input()
  get selectedColumns(): ColumnMapping[] {
    return this._selectedColumns;
  }

  set selectedColumns(selectedColumns: ColumnMapping[]) {
    if (selectedColumns === this._selectedColumns) {
      return;
    }
    this._selectedColumns = selectedColumns;
    this.selectedColumnsChange.emit(this._selectedColumns);
  }

  @Input()
  aggregatedTotalType = 'cartons';

  private _cartonGroupings: CartonGroupingViewModel[] = [];
  private _isLoading = this.loadAtStart;

  set isLoading(value: boolean) {
    this._isLoading = value;
    this.loading.emit(value);
  }

  set cartonGroupings(value: CartonGroupingViewModel[]) {
    this._cartonGroupings = value;
    this.canDownload.emit(this._cartonGroupings.length > 0);
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get cartonGroupings(): CartonGroupingViewModel[] {
    return this._cartonGroupings;
  }

  get filterArgs(): CartonGroupingsQueryVariables {
    const args: CartonGroupingSearchAttributes = {};
    const companyIdsSet = new Set<number>();

    if (this.companyId) {
      companyIdsSet.add(this.companyId);
    }

    if (this.companyIds) {
      this.companyIds.forEach((companyId) => companyIdsSet.add(companyId));
    }

    if (companyIdsSet.size > 0) {
      args.companyIds = Array.from(companyIdsSet);
    }

    if (this.orderId) {
      args.orderId = this.orderId;
    }
    if (this.packingWeekFrom) {
      args.packingWeekFrom = this.packingWeekFrom;
    }
    if (this.packingWeekTo) {
      args.packingWeekTo = this.packingWeekTo;
    }
    if (this.limit) {
      args.limit = this.limit;
    } else {
      args.limit = 1000;
    }
    return { attributes: args };
  }

  columnMappings: ColumnMapping[] = [
    { heading: 'Batch Number', property: 'batchNumber' },
    { heading: 'Buyer', property: 'buyerName' },
    { heading: 'Cold Store', property: 'coldStore' },
    { heading: 'Commodity', property: 'commodity' },
    { heading: 'Consignment Number', property: 'consignmentNumber' },
    { heading: 'Container', property: 'containerNumber' },
    { heading: 'Exporter Code', property: 'exporterCode' },
    { heading: 'Farm Code', property: 'farmCode' },
    { heading: 'Grade', property: 'grade' },
    { heading: 'Incoterm', property: 'incoterm' },
    { heading: 'Inventory Code', property: 'inventoryCode' },
    { heading: 'Line Item #', property: 'lineItemId' },
    { heading: 'Mark', property: 'mark' },
    { heading: 'Orchard', property: 'orchard' },
    { heading: 'Order #', property: 'orderId' },
    { heading: 'Pack', property: 'pack' },
    { heading: 'Packhouse Code', property: 'packhouseCode' },
    { heading: 'Packing Week', property: 'packingWeek' },
    { heading: 'Pallet Number', property: 'palletNumber' },
    { heading: 'Pallet Stack', property: 'palletStack' },
    { heading: 'Product Characteristic', property: 'productCharacteristic' },
    { heading: 'Reference Number', property: 'referenceNumber' },
    { heading: 'Seller', property: 'sellerName' },
    { heading: 'Sequence Number', property: 'sequenceNumber' },
    { heading: 'Size Count', property: 'sizeCount' },
    { heading: 'State', property: 'state' },
    { heading: 'Target Country', property: 'targetCountry' },
    { heading: 'Target Market', property: 'targetMarket' },
    { heading: 'Target Region', property: 'targetRegion' },
    { heading: 'Variety', property: 'variety' },
  ];

  aggregatedColumns: AggregatedColumn[] = [
    {
      property: 'cartons',
      aggregator: (iterable: number[]) =>
        new DecimalPipe(this.locale).transform(Aggregators.sum(iterable)),
      heading: 'Total Cartons',
    },
  ];

  constructor() {}

  ngOnInit(): void {
    setTimeout(() => {
      if (this.loadAtStart) {
        this.loadCartonGroupings();
      } else {
        this.isLoading = false;
      }
    });
  }

  loadCartonGroupings() {
    this.isLoading = true;
    this.switchAggregatedTotal();
    this.cartonGroupingsGql
      .fetch(this.filterArgs)
      .subscribe(
        (res) => {
          this.cartonGroupings =
            res.data.cartonGroupingsSearch.cartonGroupings.map(
              (cg) => new CartonGroupingViewModel(cg),
            );
        },
        (err) => {
          this.toastService.showErrorToast('Unable to load pallets');
          throw err;
        },
      )
      .add(() => {
        this.isLoading = false;
      });
  }

  downloadData() {
    ExcelHelper.downloadPivotTableData(
      'Pallets',
      this.cartonGroupings,
      this.selectedColumns,
      this.aggregatedColumns,
    );
    this.angulartics2.eventTrack.next({
      action: 'Export Pallets',
    });
  }

  switchAggregatedTotal() {
    const propertyHeading = this.titleCase.transform(
      'Total ' + this.aggregatedTotalType,
    );

    this.aggregatedColumns = [
      {
        property: this.aggregatedTotalType,
        aggregator: (iterable: number[]) =>
          new DecimalPipe(this.locale).transform(Aggregators.sum(iterable)),
        heading: propertyHeading,
      },
    ];
  }
}
